import { useState } from 'react';
import { useCrm } from '@/hooks/useCrm';
import { CrmClientCard } from '@/components/cards/CrmClientCard';
import { ClientDetailModal } from '@/components/common/ClientDetailModal';
import { SegmentSelector } from '@/components/filters/SegmentSelector';
import { SearchBar } from '@/components/filters/SearchBar';
import { GroupingMenu } from '@/components/filters/GroupingMenu';
import { SortMenu } from '@/components/filters/SortMenu';
import { CardSkeleton } from '@/components/common/SkeletonLoader';
import { Users, RefreshCw, Eye, EyeOff, ChevronDown, ChevronRight, Folder, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';
import type { CrmClient, CrmSortField, CrmGroupBy } from '@/types/crm';

const CRM_SORT_OPTIONS: Array<{ value: CrmSortField; label: string }> = [
  { value: 'name', label: 'Nombre' },
  { value: 'totalSales', label: 'Mayor consumo ($)' },
  { value: 'totalAppointments', label: 'Más visitas' },
  { value: 'daysAbsent', label: 'Días ausente' },
  { value: 'outstandingAmount', label: 'Saldo deudor' },
];

const CRM_GROUP_OPTIONS: Array<{ value: CrmGroupBy; label: string }> = [
  { value: 'none', label: 'Sin agrupar' },
  { value: 'staff', label: 'Por estilista habitual' },
  { value: 'sales_range', label: 'Por nivel de consumo' },
  { value: 'visits_range', label: 'Por frecuencia de visitas' },
  { value: 'debt_status', label: 'Por estado de deuda' },
];

export function CrmPage() {
  const {
    clients,
    groupedClients,
    segments,
    activeSegmentId,
    activeSubFilter,
    segmentCounts,
    subFilterCounts,
    loading,
    loadingPhase,
    loadedCount,
    totalCount,
    searchQuery,
    sortBy,
    sortOrder,
    groupBy,
    hideProcessed,
    statsProgress,
    fetchSegmentCustomers,
    setActiveSegmentId,
    setActiveSubFilter,
    setSearchQuery,
    setSortBy,
    setSortOrder,
    setGroupBy,
    setHideProcessed,
    markProcessed,
    unmarkProcessed,
    isProcessed,
  } = useCrm();

  const [selectedModalClient, setSelectedModalClient] = useState<CrmClient | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (groupLabel: string) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupLabel]: !prev[groupLabel],
    }));
  };

  return (
    <div className="space-y-4 py-4 max-w-6xl mx-auto w-full">
      {/* Header & Refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-text-primary">Clientes & Segmentos CRM</h2>
          <p className="text-xs text-text-muted">
            {clients.length} cliente{clients.length !== 1 ? 's' : ''} {activeSegmentId ? 'en este segmento' : 'en total'}
          </p>
        </div>
        <button
          type="button"
          onClick={() => fetchSegmentCustomers()}
          disabled={loading}
          className="p-2 rounded-lg hover:bg-surface-secondary text-text-secondary cursor-pointer disabled:opacity-50 transition-colors"
          title="Recargar clientes del segmento"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin text-brand-primary' : ''} />
        </button>
      </div>

      {/* Segment Selector with Quick Subfilter Pills and Primary Segment Dropdown */}
      <SegmentSelector
        segments={segments}
        selectedSegmentId={activeSegmentId}
        onSelectSegment={setActiveSegmentId}
        activeSubFilter={activeSubFilter}
        onSelectSubFilter={setActiveSubFilter}
        loading={loading}
        subFilterCounts={subFilterCounts}
        segmentCounts={segmentCounts}
      />

      {/* Synchronization Progress Bar */}
      {loading && (
        <div className="space-y-1 bg-surface-secondary/60 p-2.5 rounded-xl border border-border">
          <div className="flex justify-between text-[11px] text-text-muted">
            <span className="font-medium text-brand-primary flex items-center gap-1.5">
              {loadingPhase === 'customers' && (
                <>
                  <Users size={13} className="shrink-0" />
                  <span>Cargando clientes ({loadedCount}{totalCount ? `/${totalCount}` : ''})...</span>
                </>
              )}
              {loadingPhase === 'segments' && (
                <>
                  <Folder size={13} className="shrink-0" />
                  <span>Cargando grupos de clientes...</span>
                </>
              )}
              {loadingPhase === 'idle' && (
                <>
                  <Loader2 size={13} className="shrink-0 animate-spin" />
                  <span>Procesando datos de clientes...</span>
                </>
              )}
            </span>
            {totalCount > 0 && (
              <span className="font-bold text-text-primary">
                {Math.min(100, Math.round((loadedCount / totalCount) * 100))}%
              </span>
            )}
          </div>
          <div className="w-full bg-surface-tertiary rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-brand-primary h-1.5 rounded-full transition-all duration-300 animate-pulse"
              style={{
                width: totalCount
                  ? `${Math.min(100, Math.round((loadedCount / totalCount) * 100))}%`
                  : '60%',
              }}
            />
          </div>
        </div>
      )}

      {/* Background Stats Enrichment Progress Bar */}
      {!loading && statsProgress.total > 0 && statsProgress.current < statsProgress.total && (
        <div className="space-y-1 bg-surface-secondary/40 p-2.5 rounded-xl border border-border">
          <div className="flex justify-between text-[10px] text-text-muted">
            <span className="flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-brand-primary animate-pulse" />
              <span>Actualizando historial y visitas recientes...</span>
            </span>
            <span className="font-semibold text-text-primary">
              {statsProgress.current}/{statsProgress.total} ({Math.round((statsProgress.current / statsProgress.total) * 100)}%)
            </span>
          </div>
          <div className="w-full bg-surface-secondary rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-brand-primary h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${(statsProgress.current / statsProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Search Bar */}
      <SearchBar
        value={searchQuery}
        onChange={setSearchQuery}
        placeholder="Buscar por nombre, teléfono, email o estilista..."
      />

      {/* Controls: Count, Inbox Zero toggle, Grouping, Sorting */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted font-medium">
            {clients.length} cliente{clients.length !== 1 ? 's' : ''}
          </span>
          <button
            type="button"
            onClick={() => setHideProcessed(!hideProcessed)}
            className={clsx(
              'flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border transition-all cursor-pointer font-medium',
              hideProcessed
                ? 'bg-brand-primary/10 border-brand-primary text-brand-primary'
                : 'bg-surface-secondary border-border text-text-secondary hover:text-text-primary'
            )}
            title={hideProcessed ? 'Mostrando solo pendientes' : 'Mostrando todos los clientes'}
          >
            {hideProcessed ? <EyeOff size={13} /> : <Eye size={13} />}
            <span>{hideProcessed ? 'Solo pendientes' : 'Ver todos'}</span>
          </button>
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <GroupingMenu
            options={CRM_GROUP_OPTIONS}
            selected={groupBy}
            onChange={(val) => setGroupBy(val as CrmGroupBy)}
          />
          <SortMenu
            options={CRM_SORT_OPTIONS}
            selected={sortBy}
            order={sortOrder}
            onSort={(field) => setSortBy(field as CrmSortField)}
            onToggleOrder={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          />
        </div>
      </div>

      {/* Client List */}
      {loading && clients.length === 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 items-start">
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : clients.length === 0 ? (
        <div className="text-center py-12 text-text-muted bg-surface/50 border border-border rounded-2xl">
          <Users size={48} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm font-semibold text-text-primary">No se encontraron clientes</p>
          <p className="text-xs text-text-muted mt-1 max-w-xs mx-auto">
            {searchQuery
              ? 'No hay clientes que coincidan con los filtros de búsqueda aplicados.'
              : hideProcessed
              ? 'Todos los clientes de este segmento han sido marcados como procesados.'
              : 'No hay clientes registrados en este segmento.'}
          </p>
        </div>
      ) : groupBy === 'none' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 items-start">
          {clients.map((client) => (
            <CrmClientCard
              key={client.id}
              client={client}
              activeSegmentId={activeSegmentId}
              activeSubFilter={activeSubFilter}
              isProcessed={isProcessed(client.id)}
              onMarkProcessed={() =>
                isProcessed(client.id) ? unmarkProcessed(client.id) : markProcessed(client.id)
              }
              onOpenDetails={setSelectedModalClient}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedClients).map(([groupLabel, groupList]) => {
            const isCollapsed = !!collapsedGroups[groupLabel];
            return (
              <div key={groupLabel} className="space-y-2.5">
                {groupLabel && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupLabel)}
                    className="w-full flex items-center justify-between py-2.5 px-3.5 bg-surface/95 backdrop-blur-md hover:bg-surface rounded-xl border border-border mt-2 shadow-2xs cursor-pointer transition-colors text-left sticky top-0 z-10"
                  >
                    <div className="flex items-center gap-2">
                      {isCollapsed ? (
                        <ChevronRight size={15} className="text-text-muted transition-transform" />
                      ) : (
                        <ChevronDown size={15} className="text-text-muted transition-transform" />
                      )}
                      <span className="text-xs font-semibold text-text-primary">{groupLabel}</span>
                    </div>
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-surface-tertiary text-text-muted border border-border/50">
                      {groupList.length}
                    </span>
                  </button>
                )}
                {!isCollapsed && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5 items-start pt-1">
                    {groupList.map((client) => (
                      <CrmClientCard
                        key={client.id}
                        client={client}
                        activeSegmentId={activeSegmentId}
                        activeSubFilter={activeSubFilter}
                        isProcessed={isProcessed(client.id)}
                        onMarkProcessed={() =>
                          isProcessed(client.id) ? unmarkProcessed(client.id) : markProcessed(client.id)
                        }
                        onOpenDetails={setSelectedModalClient}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Client Detail Modal */}
      <ClientDetailModal
        client={selectedModalClient}
        isOpen={Boolean(selectedModalClient)}
        onClose={() => setSelectedModalClient(null)}
      />
    </div>
  );
}

